import type { TFunction } from 'i18next';
import type { Expertise } from 'api/referentialsApi';

/**
 * Wording for the two confirmations of the referentials, shared so both screens say the same
 * thing about a withdrawal. The dialog itself is `src/components/ConfirmDeleteDialog` in its
 * warning tone — deactivating is not deleting, and nothing here should read as destruction.
 */

export const deactivateCopy = (t: TFunction, name: string) => ({
  title: t('Deactivate {{name}}?', { name }),
  description: t('{{name}} stops being offered for selection.', { name }),
  // The reassurance goes in the framed block, not in the running text: it is the one thing an
  // ops has to register before clicking, and the design frames it for that reason.
  details: [
    {
      label: t('No deletion'),
      value: t(
        'The value stays visible on existing records and in the history, it is simply no longer offered for selection.'
      ),
    },
  ],
  confirmLabel: t('Deactivate'),
});

/**
 * Detaching a shared expertise is the case the mock-up performs silently: an ops tidying up
 * Installation would break Fabrication PLV without seeing it. The count of remaining specialties
 * is surfaced as a detail line, not buried.
 */
export const detachCopy = (t: TFunction, expertise: Expertise) => ({
  title: t('Detach {{name}}?', { name: expertise.name }),
  description: t('The expertise leaves this specialty. It is not deleted and keeps its other attachments.'),
  confirmLabel: t('Detach'),
  details:
    expertise.specialty_ids.length > 1
      ? [
          {
            label: t('Shared'),
            value: t('This expertise is shared with {{count}} other specialty', {
              count: expertise.specialty_ids.length - 1,
            }),
            emphasis: true,
          },
        ]
      : [],
});
